import { AssetToUploadParams, createAssetTypeSource } from '../utils/assetTypeSourceHelpers';
import { DEFAULT_RETRY_CONFIG, RETRY_CONFIGS } from "../config/retryBackoffTypes"
import { RetryConfig } from '../types';
import { captureError } from '@/utils/sentry';
import { sanitizeFilenameForUpload } from '@/utils/sanitizeFilename';
import { v1, toResult, multipart, binary, type V1Result } from '@/infrastructure/api/v1/client';
import type { ApiProblem } from '@/infrastructure/api/v1/problem';
import { uploadAssetTypeFile, type UploadedAssetFile } from '@/infrastructure/api/v1/uploads';
import type { CreateAssetTypeRequest } from '@/infrastructure/api/v1/mappers/assetTypes';
import type { AssetTypeSource } from '../types';

export interface UploadResult {
  success: boolean;
  data?: any;
  error?: string;
  generatedAssetTypeId?: string;
  cancelled?: boolean;
}

export const createRetryConfig = (overrides: Partial<RetryConfig>): RetryConfig => ({
  ...DEFAULT_RETRY_CONFIG,
  ...overrides
});

/**
 * Thrown when the BE answers with a verdict (a non-2xx status, or a failed
 * conversion). Lets retryWithBackoff bail immediately instead of retrying
 * the same dead end. A network blip (no response) is still retried, under
 * the same client id, so a retried upload never writes a second row.
 */
export class HttpResponseError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'HttpResponseError';
  }
}

const isRetryable = (error: Error): boolean => {
  if (error instanceof HttpResponseError) return false;
  if (error.name === 'AbortError') return false;
  return true;
};

/**
 * A user-initiated cancel is not a failure. Everything else that leaves the
 * retry loop is, including the non-retryable server errors that previously
 * threw without ever reaching Sentry (lureo-docs#365).
 */
const isReportable = (error: Error): boolean => error.name !== 'AbortError';

const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  operationName: string = 'Operation'
): Promise<T> => {
  let lastError: Error;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      const exhausted = attempt === config.maxRetries;
      const retryable = isRetryable(lastError);

      if (!retryable || exhausted) {
        if (isReportable(lastError)) {
          captureError(lastError, {
            domain: 'upload',
            operation: operationName,
            attempts: attempt + 1,
          });
          console.error(`${operationName} failed after ${attempt + 1} attempt(s):`, lastError);
        }
        throw lastError;
      }

      const delay = Math.min(
        config.baseDelay * Math.pow(config.backoffMultiplier, attempt),
        config.maxDelay
      );

      console.warn(`${operationName} attempt ${attempt + 1} failed, retrying in ${delay}ms:`, lastError.message);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
};

/** The uploadAssetType part for a new asset type; sourceUrl and status are the upload's to set. */
function uploadAssetTypeBody(source: AssetTypeSource): CreateAssetTypeRequest {
  return {
    uniqueId: source.asset_type_id,
    orgInstanceUniqueId: source.org_instance_id || undefined,
    displayName: source.asset_type_display_name,
    sourceName: source.asset_type_source_name,
    category: source.asset_type_category,
    // No tags and no animations are left unset rather than stored empty
    tags: source.asset_type_tags?.length ? source.asset_type_tags : undefined,
    details: source.asset_type_details,
    // The wizard holds a missing URL as ''; v1 takes only absolute URLs, so a missing one is left unset
    imgUrl: source.asset_type_img_url || undefined,
    animations: source.asset_type_animations && Object.keys(source.asset_type_animations).length ? source.asset_type_animations : undefined,
    optimizedUrl: source.asset_type_optimized_url || undefined,
    position: source.asset_type_position,
    rotation: source.asset_type_rotation,
    scale: source.asset_type_scale,
    isActive: source.asset_type_is_active,
    beginTime: source.asset_type_begin_time,
    endTime: source.asset_type_end_time,
    displayOn: source.asset_type_display_on,
    availability: source.asset_type_availability,
  };
}

/** The error a failed v1 call throws here: a verdict stops the retries, a missing response does not. */
function callError(error: ApiProblem, what: string): Error {
  const message = `${what} failed: ${error.status} ${error.title}`;
  return error.status === 0 ? new Error(message) : new HttpResponseError(error.status, message);
}

export const uploadAsset = async (
  params: AssetToUploadParams,
  abortSignal?: AbortSignal
): Promise<UploadResult> => {
  const { file } = params;
  const safeName = sanitizeFilenameForUpload(file.name);
  const fileWithSafeName = new File([file], safeName, { type: file.type });

  // Built once, so every retry sends the same client id: a repeat is answered with the type the first attempt made
  const assetTypeSource = await createAssetTypeSource({ ...params, file: fileWithSafeName });
  const assetType = JSON.stringify({ ...uploadAssetTypeBody(assetTypeSource), ...(params.edits ? { edits: params.edits } : {}) });
  // Kept across retries once stored, so a retry records the same file rather than sending it again
  let direct: V1Result<UploadedAssetFile> | null | undefined;

  try {
    return await retryWithBackoff(async () => {
      // Straight to storage into the new type's folder, never through the backend; null while the deployment is still
      // on Supabase Storage, and the file goes in the form as before
      const stored = (direct ??= await uploadAssetTypeFile(assetTypeSource.asset_type_id, safeName, fileWithSafeName, abortSignal));
      if (abortSignal?.aborted) throw new DOMException('Upload cancelled by user', 'AbortError');
      if (stored?.error) {
        direct = undefined;
        throw callError(stored.error, 'Upload');
      }

      const { data, error } = await toResult(v1.POST('/api/v1/asset-types/uploads', {
        body: stored
          ? { uploadUniqueId: stored.data.uploadUniqueId, assetType }
          : { file: binary(fileWithSafeName), assetType },
        bodySerializer: multipart,
        signal: abortSignal,
      }));
      if (abortSignal?.aborted) throw new DOMException('Upload cancelled by user', 'AbortError');
      if (error) {
        console.error('Backend error response:', error);
        throw callError(error, 'Upload');
      }
      return {
        success: true,
        data,
        generatedAssetTypeId: assetTypeSource.asset_type_id
      };
    }, RETRY_CONFIGS.UPLOAD, 'Upload');
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        error: 'Upload cancelled by user',
        cancelled: true
      };
    }
    throw error;
  }
};

// Compression modes the BE recognises on the converter endpoint:
// - 'meshopt' → gltfpack -cc post-process (EXT_meshopt_compression).
//   Default for everything that isn't on the broken-list below.
// - 'draco'   → Blender's KHR_draco_mesh_compression. Currently UNUSED
//   for animated text. Empirically broken in Unity glTFast for
//   bone-translation-dense rigs (wave / ripple) the same way Meshopt
//   is — so for those animations we skip conversion entirely
//   (see convertWithAnimationRouting below). Kept available on the
//   API for future cases where it's the right answer.
// - undefined → no compression. Safe for any caller.
export type CompressMode = 'meshopt' | 'draco';

export interface ConversionProgress {
  status: 'in_progress' | 'completed' | 'failed';
  elapsedSeconds: number;
  attempt: number;
}

const CONVERSION_FAST_POLL_INTERVAL_MS = 400;
const CONVERSION_FAST_POLL_WINDOW_MS = 15_000;
const CONVERSION_POLL_INTERVAL_MS = 3000;
const CONVERSION_POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Answers a proxy can return in place of the backend's own. A hosting gateway's
 * timeout is the reason this endpoint became a poll at all, so a 502/503/504
 * here is an interruption rather than a verdict on the model and keeps its
 * backoff. Only the backend's own failure response is terminal.
 */
const TRANSIENT_POLL_STATUSES = new Set([408, 425, 429, 502, 503, 504]);

// Backend conversion is async: ffmpeg / blender / gltfpack runs in a
// background DI scope. A POST starts it (or returns the one already tracked)
// and a GET polls it: 202 while pending / inProgress, 200 once completed or
// failed (the old synchronous wait is what was 504-ing at the hosting gateway
// when CORS appeared "broken"). A GET 404 means nothing is tracked (a server
// restart), so it is started again. The onProgress callback fires every poll
// so the upload UI can show elapsed time and stop the user thinking it's hung.
export const convertAsset = async (
  assetTypeId: string | undefined,
  compress?: CompressMode,
  onProgress?: (p: ConversionProgress) => void,
): Promise<UploadResult> => {
  if (!assetTypeId) {
    return {
      success: false,
      error: 'Asset type ID is required for conversion'
    };
  }

  const path = { params: { path: { assetTypeUniqueId: assetTypeId } } };
  const start = () => v1.POST('/api/v1/asset-types/{assetTypeUniqueId}/conversion', { ...path, body: { compress: compress ?? 'none' } });
  const startedAt = Date.now();
  let attempt = 0;

  const poll = async (): Promise<UploadResult> => {
    let starting = true;
    let pending = start();
    while (true) {
      attempt += 1;
      const { data, error } = await toResult(pending);

      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);

      if (error?.status === 404 && !starting) {
        starting = true;
        pending = start();
        continue;
      }

      if (error) {
        console.error('Converter error response:', error);
        onProgress?.({ status: 'failed', elapsedSeconds, attempt });
        // A gateway blip stays retryable; the backend's own refusal stops the backoff loop
        throw TRANSIENT_POLL_STATUSES.has(error.status)
          ? new Error(`Conversion failed: ${error.status} ${error.title}`)
          : callError(error, 'Conversion');
      }

      if (data.status === 'pending' || data.status === 'inProgress') {
        onProgress?.({ status: 'in_progress', elapsedSeconds, attempt });

        if (Date.now() - startedAt > CONVERSION_POLL_TIMEOUT_MS) {
          throw new Error(`Conversion still in progress after ${Math.round(CONVERSION_POLL_TIMEOUT_MS / 60000)} minutes — giving up`);
        }

        const interval = Date.now() - startedAt < CONVERSION_FAST_POLL_WINDOW_MS
          ? CONVERSION_FAST_POLL_INTERVAL_MS
          : CONVERSION_POLL_INTERVAL_MS;
        await new Promise(resolve => setTimeout(resolve, interval));
        starting = false;
        pending = v1.GET('/api/v1/asset-types/{assetTypeUniqueId}/conversion', path);
        continue;
      }

      if (data.status === 'failed') {
        onProgress?.({ status: 'failed', elapsedSeconds, attempt });
        // A failed conversion is a verdict, not a blip: retrying re-ran the
        // whole Blender job five more times for the same dead end (lureo-docs#365)
        throw new HttpResponseError(200, `Conversion failed: ${data.error ?? 'unknown error'}`);
      }

      onProgress?.({ status: 'completed', elapsedSeconds, attempt });
      return {
        success: true,
        data: data.files ?? [],
        generatedAssetTypeId: assetTypeId
      };
    }
  };

  return retryWithBackoff(poll, RETRY_CONFIGS.CONVERSION, 'Conversion');
};

// Animations that don't survive any post-upload encoding (Meshopt OR Draco)
// in Unity glTFast 6.14.1. These animate bone.translation continuously
// around a non-zero absolute base (cx + amplitude × sin(t)); the
// quantization in either compressor collapses the small ±amplitude
// variation into noise on top of the larger absolute base, and Unity
// renders the result in the wrong reference frame.
//
// HEY FUTURE ME — adding a new text animation:
// 1. Build it in src/modules/primitives3D/utils/textAnimations.ts.
// 2. Run a marketplace upload + render in the Unity build.
// 3. If it plays correctly under Meshopt, no code change needed
//    (Meshopt is the default below).
// 4. If it plays in the wrong reference frame, add the animation's
//    name here. The upload then routes to skipConversion automatically
//    — no Blender, no compression — and the asset ships uncompressed.
//
// The pattern that breaks: continuous / dense bone.translation
// oscillation. Sparse reveal-and-hold patterns and bone.scale
// animations have all been fine under Meshopt.
export const SKIP_CONVERSION_ANIMATIONS: ReadonlySet<string> = new Set(['wave', 'ripple']);

/**
 * Read the first animation's name from a GLB blob's JSON chunk.
 * Returns undefined if the blob has no animations or can't be parsed.
 * Used by convertWithAnimationRouting below to decide whether the
 * asset needs the skip-conversion path.
 */
export async function readFirstAnimationName(blob: Blob): Promise<string | undefined> {
  try {
    // The JSON chunk is at the front of the GLB; 1 MB is dramatically
    // more than any realistic chunk size. Avoids loading multi-MB blobs
    // into a single ArrayBuffer just to peek at the header.
    const head = await blob.slice(0, 1_000_000).arrayBuffer();
    const view = new DataView(head);
    if (view.getUint32(0, true) !== 0x46546c67 /* 'glTF' little-endian */) return undefined;
    const jsonLen = view.getUint32(12, true);
    if (jsonLen + 20 > head.byteLength) return undefined;
    const jsonBytes = new Uint8Array(head, 20, jsonLen);
    const json = JSON.parse(new TextDecoder().decode(jsonBytes));
    return json?.animations?.[0]?.name;
  } catch {
    return undefined;
  }
}

export const skipConversion = async (assetTypeId: string | undefined): Promise<UploadResult> => {
  if (!assetTypeId) {
    return {
      success: false,
      error: 'Asset type ID is required for skip conversion'
    };
  }

  return retryWithBackoff(async () => {
    const { data, error } = await toResult(v1.POST('/api/v1/asset-types/{assetTypeUniqueId}/conversion/skip', {
      params: { path: { assetTypeUniqueId: assetTypeId } },
    }));
    if (error) {
      console.error('Skip conversion error response:', error);
      throw new Error(`Skip conversion failed: ${error.status} ${error.title}`);
    }

    return {
      success: true,
      data: data.files ?? [],
      generatedAssetTypeId: assetTypeId
    };
  }, RETRY_CONFIGS.CONVERSION, 'SkipConversion');
};

export const uploadExtra = async (
  file: File,
  assetTypeId: string,
  assetFilename: string
): Promise<UploadResult> => {
  const safeFilename = sanitizeFilenameForUpload(assetFilename);
  const fileWithSafeName = new File([file], safeFilename, { type: file.type });
  // Kept across retries once stored, so a retry records the same file rather than sending it again
  let direct: V1Result<UploadedAssetFile> | null | undefined;

  return retryWithBackoff(async () => {
    // Straight to storage into the type's folder, never through the backend; null while the deployment is still on
    // Supabase Storage, and the file goes in the form as before
    const stored = (direct ??= await uploadAssetTypeFile(assetTypeId, safeFilename, fileWithSafeName));
    if (stored?.error) {
      direct = undefined;
      throw callError(stored.error, 'Upload-extra');
    }

    // The same name replaces the file, so a retry is safe
    const { data, error } = await toResult(v1.PUT('/api/v1/asset-types/{assetTypeUniqueId}/files/{fileName}', {
      params: { path: { assetTypeUniqueId: assetTypeId, fileName: safeFilename } },
      body: stored ? { uploadUniqueId: stored.data.uploadUniqueId } : { file: binary(fileWithSafeName) },
      bodySerializer: multipart,
    }));
    if (error) {
      console.error('Upload-extra error response:', error);
      throw new Error(`Upload-extra failed: ${error.status} ${error.title}`);
    }

    return {
      success: true,
      data,
      generatedAssetTypeId: assetTypeId
    };
  }, RETRY_CONFIGS.UPLOAD, 'Upload-extra');
};
